<template>
  <v-card-text class="pa-6">
    <!-- Quick Stats -->
    <v-row class="mb-6">
      <v-col cols="12" md="4">
        <v-card outlined class="text-center pa-4 stats-card">
          <v-icon color="primary" size="32" class="mb-2">mdi-calendar-check</v-icon>
          <div class="text-caption grey--text">วันที่บรรจุ</div>
          <div class="text-h6 font-weight-medium">{{ formatDate(employee.date_app_now) || '-' }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" md="4">
        <v-card outlined class="text-center pa-4 stats-card">
          <v-icon color="success" size="32" class="mb-2">mdi-calendar-start</v-icon>
          <div class="text-caption grey--text">สัญญาปัจจุบัน</div>
          <div class="text-h6 font-weight-medium">{{ formatDate(employee.date_app_now) || '-' }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" md="4">
        <v-card outlined class="text-center pa-4 stats-card">
          <v-icon color="error" size="32" class="mb-2">mdi-calendar-end</v-icon>
          <div class="text-caption grey--text">สิ้นสุดสัญญา</div>
          <div class="text-h6 font-weight-medium">{{ formatDate(employee.date_end_govem) || '-' }}</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Main Form -->
    <v-form ref="basicForm" v-model="formValid" lazy-validation>
      
      <!-- Personal Information Section -->
      <v-card outlined class="mb-6">
        <v-card-title class="primary--text">
          <v-icon left color="primary">mdi-account</v-icon>
          ข้อมูลส่วนบุคคล
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="formData.salary_s"
                label="เงินเดือน (บาท)"
                type="number"
                outlined
                dense
                prepend-inner-icon="mdi-cash"
                :rules="salaryRules"
              />
            </v-col>
            
            <v-col cols="12" md="4">
              <v-autocomplete
                v-model="formData.sex"
                :items="genderOptions"
                label="เพศ"
                outlined
                dense
                prepend-inner-icon="mdi-human-male-female"
              />
            </v-col>
            
            <v-col cols="12" md="4">
              <v-autocomplete
                v-model="formData.marital_status"
                :items="maritalStatusOptions"
                label="สถานภาพ"
                outlined
                dense
                prepend-inner-icon="mdi-heart"
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.e_mail"
                label="อีเมล"
                type="email"
                outlined
                dense
                prepend-inner-icon="mdi-email"
                :rules="emailRules"
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.tel_p"
                label="เบอร์โทรศัพท์มือถือ"
                outlined
                dense
                prepend-inner-icon="mdi-cellphone"
                :rules="phoneRules"
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Domicile Information -->
      <v-card outlined class="mb-6">
        <v-card-title class="success--text">
          <v-icon left color="success">mdi-home-map-marker</v-icon>
          ภูมิลำเนา
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="domicileProvinceId"
                :items="allProvinces"
                item-text="province_name"
                item-value="province_ID"
                label="จังหวัดภูมิลำเนา"
                outlined
                dense
                :loading="provincesLoading"
                prepend-inner-icon="mdi-map-marker"
                @change="onDomicileProvinceChange"
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Current Address Section -->
      <v-card outlined class="mb-6">
        <v-card-title class="info--text">
          <v-icon left color="info">mdi-map-marker-multiple</v-icon>
          ที่อยู่ปัจจุบัน
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="formData.pt_homeid"
                label="บ้านเลขที่"
                outlined
                dense
              />
            </v-col>
            
            <v-col cols="12" md="4">
              <v-text-field
                v-model="formData.pt_moo"
                label="หมู่ที่"
                outlined
                dense
              />
            </v-col>
            
            <v-col cols="12" md="4">
              <v-text-field
                v-model="formData.pt_road"
                label="ถนน/ซอย"
                outlined
                dense
              />
            </v-col>

            <!-- Province Selection -->
            <v-col cols="12" md="4">
              <v-autocomplete
                v-model="addressProvinceId"
                :items="allProvinces"
                item-text="province_name"
                item-value="province_ID"
                label="จังหวัด"
                outlined
                dense
                :loading="provincesLoading"
                prepend-inner-icon="mdi-city"
                @change="onAddressProvinceChange"
                hint="เลือกจังหวัดก่อน"
                persistent-hint
              />
            </v-col>

            <!-- Prefecture Selection -->
            <v-col cols="12" md="4">
              <v-autocomplete
                v-model="addressPrefectureId"
                :items="addressPrefectures"
                item-text="prefecture_name"
                item-value="prefecture_ID"
                label="อำเภอ/เขต"
                outlined
                dense
                :loading="prefecturesLoading"
                :disabled="!addressProvinceId"
                prepend-inner-icon="mdi-office-building"
                @change="onAddressPrefectureChange"
                hint="เลือกจังหวัดก่อน"
                persistent-hint
                clearable
              />
            </v-col>

            <!-- District Selection -->
            <v-col cols="12" md="4">
              <v-autocomplete
                v-model="addressDistrictId"
                :items="addressDistricts"
                item-text="district_name"
                item-value="district_ID"
                label="ตำบล/แขวง"
                outlined
                dense
                :loading="districtsLoading"
                :disabled="!addressPrefectureId"
                prepend-inner-icon="mdi-town-hall"
                @change="onAddressDistrictChange"
                hint="เลือกอำเภอก่อน"
                persistent-hint
                clearable
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Contact Information -->
      <v-card outlined class="mb-6">
        <v-card-title class="orange--text">
          <v-icon left color="orange">mdi-phone-settings</v-icon>
          ข้อมูลการติดต่อเพิ่มเติม
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.pt_hometel"
                label="โทรศัพท์บ้าน"
                outlined
                dense
                prepend-inner-icon="mdi-phone"
                :rules="phoneRules"
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.pt_fax"
                label="โทรสาร (แฟกซ์)"
                outlined
                dense
                prepend-inner-icon="mdi-fax"
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Profile Image Section -->
      <v-card outlined class="mb-6">
        <v-card-title class="purple--text">
          <v-icon left color="purple">mdi-camera-account</v-icon>
          รูปโปรไฟล์
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="6">
              <!-- Current Image Display -->
              <div v-if="employee.personnel_temporary_pic && !imagePreview" class="mb-4">
                <div class="text-subtitle-2 mb-2">รูปปัจจุบัน</div>
                <v-img
                  :src="`/HRvecfiles/${employee.personnel_temporary_pic}`"
                  max-height="200"
                  max-width="200"
                  class="profile-image"
                >
                  <template v-slot:placeholder>
                    <v-row class="fill-height ma-0" align="center" justify="center">
                      <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
                    </v-row>
                  </template>
                </v-img>
              </div>

              <v-file-input
                v-model="profileImageFile"
                label="เลือกรูปโปรไฟล์ใหม่"
                accept="image/*"
                outlined
                dense
                prepend-icon="mdi-camera"
                show-size
                :rules="imageRules"
                @change="previewImage"
              />
            </v-col>

            <v-col cols="12" md="6" v-if="imagePreview">
              <div class="text-subtitle-2 mb-2">ตัวอย่างรูปใหม่</div>
              <v-img
                :src="imagePreview"
                max-height="200"
                max-width="200"
                class="profile-image"
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Save Button -->
      <div class="text-center">
        <v-btn
          color="primary"
          large
          :loading="saving"
          :disabled="!formValid || saving"
          @click="handleSave"
          class="px-8"
        >
          <v-icon left>mdi-content-save</v-icon>
          บันทึกข้อมูล
        </v-btn>
      </div>

    </v-form>
  </v-card-text>
</template>

<script>
export default {
  name: 'BasicInfo',
  
  props: {
    employee: {
      type: Object,
      default: () => ({})
    },
    saving: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      formValid: false,
      ApiKey: 'HRvec2021',
      
      // Form data
      formData: {
        salary_s: null,
        sex: '',
        e_mail: '',
        tel_p: '',
        marital_status: '',
        pt_homeid: '',
        pt_moo: '',
        pt_road: '',
        pt_hometel: '',
        pt_fax: ''
      },
      
      // Image handling
      profileImageFile: null,
      imagePreview: null,
      
      // Address data
      allProvinces: [],
      addressPrefectures: [],
      addressDistricts: [],
      
      // Selection tracking
      domicileProvinceId: null,
      addressProvinceId: null,
      addressPrefectureId: null,
      addressDistrictId: null,
      
      // Loading states
      provincesLoading: false,
      prefecturesLoading: false,
      districtsLoading: false,
      
      // Options
      genderOptions: [
        { text: 'ชาย', value: 'ชาย' },
        { text: 'หญิง', value: 'หญิง' }
      ],
      
      maritalStatusOptions: [
        { text: 'โสด', value: 'โสด' },
        { text: 'สมรส', value: 'สมรส' },
        { text: 'หย่าร้าง', value: 'หย่าร้าง' },
        { text: 'หม้าย', value: 'หม้าย' },
        { text: 'แยกกันอยู่', value: 'แยกกันอยู่' }
      ],
      
      // Validation rules
      salaryRules: [
        v => !v || v >= 0 || 'เงินเดือนต้องไม่น้อยกว่า 0'
      ],
      emailRules: [
        v => !v || /.+@.+\..+/.test(v) || 'รูปแบบอีเมลไม่ถูกต้อง'
      ],
      phoneRules: [
        v => !v || /^[0-9-+().\s]{8,15}$/.test(v) || 'รูปแบบเบอร์โทรไม่ถูกต้อง'
      ],
      imageRules: [
        v => !v || v.size < 5000000 || 'ไฟล์รูปภาพต้องมีขนาดไม่เกิน 5MB',
        v => !v || ['image/jpeg', 'image/jpg', 'image/png'].includes(v.type) || 'รองรับเฉพาะไฟล์ JPG และ PNG'
      ]
    }
  },
  
  async mounted() {
    await this.loadProvinces()
  },
  
  watch: {
    employee: {
      async handler(newEmployee) {
        if (!newEmployee || !newEmployee.id_card) return
        
        console.log('Employee data changed:', newEmployee)
        
        // Update form data
        this.formData = {
          salary_s: newEmployee.salary_s || null,
          sex: newEmployee.sex || '',
          e_mail: newEmployee.e_mail || '',
          tel_p: newEmployee.tel_p || '',
          marital_status: newEmployee.marital_status || '',
          pt_homeid: newEmployee.pt_homeid || '',
          pt_moo: newEmployee.pt_moo || '',
          pt_road: newEmployee.pt_road || '',
          pt_hometel: newEmployee.pt_hometel || '',
          pt_fax: newEmployee.pt_fax || ''
        }
        
        // Set address selections
        this.domicileProvinceId = newEmployee.domicile_pt || null
        this.addressProvinceId = newEmployee.pt_province || null
        this.addressPrefectureId = newEmployee.pt_perfecture || null
        this.addressDistrictId = newEmployee.pt_distirict || null
        
        // Load cascading data if exists
        if (this.addressProvinceId) {
          await this.loadPrefectures(this.addressProvinceId)
          
          if (this.addressPrefectureId) {
            await this.loadDistricts(this.addressPrefectureId)
          }
        }
      },
      immediate: true,
      deep: true
    }
  },
  
  methods: {
    // API Methods
    async loadProvinces() {
      try {
        this.provincesLoading = true
        console.log('Loading provinces...')
        
        const response = await this.$http.post('province.php', {})
        
        if (response.data && Array.isArray(response.data)) {
          this.allProvinces = response.data
          console.log('Provinces loaded:', this.allProvinces.length)
        } else {
          console.log('No provinces data received')
          this.allProvinces = []
        }
      } catch (error) {
        console.error('Load provinces error:', error)
        this.allProvinces = []
      } finally {
        this.provincesLoading = false
      }
    },
    
    async loadPrefectures(provinceId) {
      if (!provinceId) return
      
      try {
        this.prefecturesLoading = true
        console.log('Loading prefectures for province:', provinceId)
        
        const response = await this.$http.post('prefecture.php', {
          province_ID: provinceId
        })
        
        if (response.data && Array.isArray(response.data)) {
          this.addressPrefectures = response.data
          console.log('Prefectures loaded:', this.addressPrefectures.length)
        } else {
          console.log('No prefectures data received')
          this.addressPrefectures = []
        }
      } catch (error) {
        console.error('Load prefectures error:', error)
        this.addressPrefectures = []
      } finally {
        this.prefecturesLoading = false
      }
    },
    
    async loadDistricts(prefectureId) {
      if (!prefectureId) return
      
      try {
        this.districtsLoading = true
        console.log('Loading districts for prefecture:', prefectureId)
        
        const response = await this.$http.post('districts.php', {
          prefecture_ID: prefectureId
        })
        
        if (response.data && Array.isArray(response.data)) {
          this.addressDistricts = response.data
          console.log('Districts loaded:', this.addressDistricts.length)
        } else {
          console.log('No districts data received')
          this.addressDistricts = []
        }
      } catch (error) {
        console.error('Load districts error:', error)
        this.addressDistricts = []
      } finally {
        this.districtsLoading = false
      }
    },
    
    // Event Handlers
    onDomicileProvinceChange(provinceId) {
      console.log('Domicile province changed:', provinceId)
      this.domicileProvinceId = provinceId
    },
    
    async onAddressProvinceChange(provinceId) {
      console.log('Address province changed:', provinceId)
      
      // Reset dependent selections
      this.addressPrefectureId = null
      this.addressDistrictId = null
      this.addressPrefectures = []
      this.addressDistricts = []
      
      if (provinceId) {
        await this.loadPrefectures(provinceId)
      }
    },
    
    async onAddressPrefectureChange(prefectureId) {
      console.log('Address prefecture changed:', prefectureId)
      
      // Reset dependent selections
      this.addressDistrictId = null
      this.addressDistricts = []
      
      if (prefectureId) {
        await this.loadDistricts(prefectureId)
      }
    },
    
    onAddressDistrictChange(districtId) {
      console.log('Address district changed:', districtId)
    },
    
    // Image Handling
    previewImage(file) {
      if (!file) {
        this.imagePreview = null
        return
      }
      
      const reader = new FileReader()
      reader.onload = (e) => {
        this.imagePreview = e.target.result
      }
      reader.readAsDataURL(file)
    },
    
    async uploadImage() {
      if (!this.profileImageFile || !this.employee.id_card) {
        return null
      }
      
      try {
        const formData = new FormData()
        const timeStamp = Date.now()
        const filename = `${this.employee.id_card}.${timeStamp}.users.jpg`
        
        formData.append("ApiKey", this.ApiKey)
        formData.append("file", this.profileImageFile)
        formData.append("filename", `../HRvecfiles/${filename}`)
        
        const result = await this.$http.post("uploadfile.php", formData, {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        })
        
        if (result.data && result.data.status) {
          return filename
        } else {
          throw new Error(result.data.message || 'Upload failed')
        }
      } catch (error) {
        console.error('Image upload error:', error)
        throw new Error('เกิดข้อผิดพลาดในการอัปโหลดรูปภาพ')
      }
    },
    
    // Form Handling
    async handleSave() {
      if (!this.$refs.basicForm.validate()) {
        return
      }
      
      try {
        // Upload image if selected
        let uploadedImageName = null
        if (this.profileImageFile) {
          uploadedImageName = await this.uploadImage()
        }
        
        // Prepare complete form data
        const completeFormData = {
          ...this.formData,
          domicile_pt: this.domicileProvinceId,
          pt_province: this.addressProvinceId,
          pt_perfecture: this.addressPrefectureId,
          pt_distirict: this.addressDistrictId
        }
        
        // Add uploaded image name if successful
        if (uploadedImageName) {
          completeFormData.personnel_temporary_pic = uploadedImageName
        }
        
        // Emit save event
        this.$emit('save', {
          basicData: completeFormData,
          profileImageFile: this.profileImageFile,
          uploadedImageName: uploadedImageName
        })
        
        // Reset image form
        this.resetImageForm()
        
      } catch (error) {
        console.error('Save error:', error)
        this.$emit('error', error.message || 'เกิดข้อผิดพลาดในการบันทึกข้อมูล')
      }
    },
    
    resetImageForm() {
      this.profileImageFile = null
      this.imagePreview = null
    },
    
    // Utility Methods
    formatDate(dateString) {
      if (!dateString) return ''
      try {
        return new Date(dateString).toLocaleDateString('th-TH', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      } catch {
        return dateString
      }
    }
  }
}
</script>

<style scoped>
/* Stats cards */
.stats-card {
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.stats-card:hover {
  border-color: #1976d2;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.15);
}

/* Profile image */
.profile-image {
  border-radius: 12px;
  border: 2px solid #e0e0e0;
  overflow: hidden;
}

/* Card styling */
.v-card.v-card--outlined {
  border: 2px solid #f5f5f5;
  transition: all 0.3s ease;
}

.v-card.v-card--outlined:hover {
  border-color: #e3f2fd;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Form field spacing */
.v-text-field,
.v-autocomplete,
.v-file-input {
  margin-bottom: 0;
}

/* Card title styling */
.v-card__title {
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 16px;
}

/* Dense form styling */
.v-input--dense .v-input__control {
  min-height: 48px;
}

/* Button styling */
.v-btn {
  text-transform: none;
  font-weight: 500;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .v-card-text {
    padding: 16px;
  }
  
  .stats-card {
    margin-bottom: 16px;
  }
}
</style>